import React, { ReactNode, useState } from "react"
import { Box, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { SIDE_MENU, USER_TYPE } from "src/modules/const"
import { ShowProgress } from "src/states/showProgress"
import { useNavigate, useParams } from "react-router"
import { useRecoilState } from "recoil"
import { useAuth } from "src/providers/LoginProvider"
import { Menu } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "src/components/ui/sheet"
import "./index.scss"

type Props = {
  menu: number
  children: ReactNode
}

export const Layout = ({ menu, children }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "side_menu" })
  const { companyId } = useParams()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  const [, setShowProgress] = useRecoilState(ShowProgress)

  const handleLogout = async () => {
    setShowProgress(true)
    logout()
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    setIsOpen(false)
  }

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-40 flex items-center justify-between px-4">
        <img src="/triple_talent.svg" alt="logo" className="h-8" />
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[250px] p-0">
            <div className="p-2.5 h-full overflow-y-auto">
              <img src="/triple_talent.svg" alt="logo" className="logo" />
              {user?.type == USER_TYPE.ADMIN ? (
                <>
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.COMPANY_LIST ? "menu-item-selected" : ""}`}
                    onClick={() => handleNavigate("/company")}
                  >
                    {t("company_list")}
                  </Box>
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.COMPANY_ADD ? "menu-item-selected" : ""}`}
                    onClick={() => handleNavigate("/company/register")}
                  >
                    {t("company_register")}
                  </Box>
                  {companyId ? (
                    <>
                      <Box
                        className={`menu-item ${
                          menu === SIDE_MENU.EMPLOYEE_LIST ? "menu-item-selected" : ""
                        }`}
                        onClick={() => handleNavigate(`/company/${companyId}/employee`)}
                      >
                        {t("employee_list")}
                      </Box>
                      <Box
                        className={`menu-item ${
                          menu === SIDE_MENU.EMPLOYEE_ADD ? "menu-item-selected" : ""
                        }`}
                        onClick={() => handleNavigate(`/company/${companyId}/employee/register`)}
                      >
                        {t("employee_register")}
                      </Box>
                      <Box
                        className={`menu-item ${menu === SIDE_MENU.TAG_LIST ? "menu-item-selected" : ""}`}
                        onClick={() => handleNavigate(`/company/${companyId}/tag`)}
                      >
                        {t("tag_list")}
                      </Box>
                      <Box
                        className={`menu-item ${
                          menu === SIDE_MENU.COMPANY_INFO ? "menu-item-selected" : ""
                        }`}
                        onClick={() => handleNavigate(`/company/${companyId}/info`)}
                      >
                        {t("company_setting")}
                      </Box>
                    </>
                  ) : (
                    <></>
                  )}
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.MY_PAGE ? "menu-item-selected" : ""}`}
                    onClick={() => handleNavigate("/mypage")}
                  >
                    {t("my_page")}
                  </Box>
                  <Box
                    className={`menu-item ${
                      menu === SIDE_MENU.PASSWORD_CHANGE ? "menu-item-selected" : ""
                    }`}
                    onClick={() => handleNavigate("/password")}
                  >
                    {t("password_change")}
                  </Box>
                </>
              ) : user?.type == USER_TYPE.GROUPADMIN ? (
                <>
                  <Box
                    className={`menu-item ${
                      menu === SIDE_MENU.EMPLOYEE_LIST ? "menu-item-selected" : ""
                    }`}
                    onClick={() => handleNavigate(`/company/${user.companyID}/employee`)}
                  >
                    {t("employee_list")}
                  </Box>
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.EMPLOYEE_ADD ? "menu-item-selected" : ""}`}
                    onClick={() => handleNavigate(`/company/${user.companyID}/employee/register`)}
                  >
                    {t("employee_register")}
                  </Box>
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.TAG_LIST ? "menu-item-selected" : ""}`}
                    onClick={() => handleNavigate(`/company/${user.companyID}/tag`)}
                  >
                    {t("tag_list")}
                  </Box>
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.COMPANY_INFO ? "menu-item-selected" : ""}`}
                    onClick={() => handleNavigate(`/company/${user.companyID}/info`)}
                  >
                    {t("company_setting")}
                  </Box>
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.MY_PAGE ? "menu-item-selected" : ""}`}
                    onClick={() => handleNavigate("/mypage")}
                  >
                    {t("my_page")}
                  </Box>
                  <Box
                    className={`menu-item ${
                      menu === SIDE_MENU.PASSWORD_CHANGE ? "menu-item-selected" : ""
                    }`}
                    onClick={() => handleNavigate("/password")}
                  >
                    {t("password_change")}
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.MY_PAGE ? "menu-item-selected" : ""}`}
                    onClick={() => handleNavigate("/mypage")}
                  >
                    {t("my_page")}
                  </Box>
                  <Box
                    className={`menu-item ${
                      menu === SIDE_MENU.PASSWORD_CHANGE ? "menu-item-selected" : ""
                    }`}
                    onClick={() => handleNavigate("/password")}
                  >
                    {t("password_change")}
                  </Box>
                </>
              )}
              <Typography className="menu-logout" onClick={() => handleLogout()}>
                {t("logout")}
              </Typography>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop and Mobile Layout */}
      <Box className="main-container lg:pt-0 pt-16">
        {/* Desktop Sidebar */}
        <Box className="menu-container hidden lg:block">
          <img src="/triple_talent.svg" alt="logo" className="logo" />
          {user?.type == USER_TYPE.ADMIN ? (
            <>
              <Box
                className={`menu-item ${menu === SIDE_MENU.COMPANY_LIST ? "menu-item-selected" : ""}`}
                onClick={() => navigate("/company")}
              >
                {t("company_list")}
              </Box>
              <Box
                className={`menu-item ${menu === SIDE_MENU.COMPANY_ADD ? "menu-item-selected" : ""}`}
                onClick={() => navigate("/company/register")}
              >
                {t("company_register")}
              </Box>
              {companyId ? (
                <>
                  <Box
                    className={`menu-item ${
                      menu === SIDE_MENU.EMPLOYEE_LIST ? "menu-item-selected" : ""
                    }`}
                    onClick={() => navigate(`/company/${companyId}/employee`)}
                  >
                    {t("employee_list")}
                  </Box>
                  <Box
                    className={`menu-item ${
                      menu === SIDE_MENU.EMPLOYEE_ADD ? "menu-item-selected" : ""
                    }`}
                    onClick={() => navigate(`/company/${companyId}/employee/register`)}
                  >
                    {t("employee_register")}
                  </Box>
                  <Box
                    className={`menu-item ${menu === SIDE_MENU.TAG_LIST ? "menu-item-selected" : ""}`}
                    onClick={() => navigate(`/company/${companyId}/tag`)}
                  >
                    {t("tag_list")}
                  </Box>
                  <Box
                    className={`menu-item ${
                      menu === SIDE_MENU.COMPANY_INFO ? "menu-item-selected" : ""
                    }`}
                    onClick={() => navigate(`/company/${companyId}/info`)}
                  >
                    {t("company_setting")}
                  </Box>
                </>
              ) : (
                <></>
              )}
              <Box
                className={`menu-item ${menu === SIDE_MENU.MY_PAGE ? "menu-item-selected" : ""}`}
                onClick={() => navigate("/mypage")}
              >
                {t("my_page")}
              </Box>
              <Box
                className={`menu-item ${
                  menu === SIDE_MENU.PASSWORD_CHANGE ? "menu-item-selected" : ""
                }`}
                onClick={() => navigate("/password")}
              >
                {t("password_change")}
              </Box>
            </>
          ) : user?.type == USER_TYPE.GROUPADMIN ? (
            <>
              <Box
                className={`menu-item ${
                  menu === SIDE_MENU.EMPLOYEE_LIST ? "menu-item-selected" : ""
                }`}
                onClick={() => navigate(`/company/${user.companyID}/employee`)}
              >
                {t("employee_list")}
              </Box>
              <Box
                className={`menu-item ${menu === SIDE_MENU.EMPLOYEE_ADD ? "menu-item-selected" : ""}`}
                onClick={() => navigate(`/company/${user.companyID}/employee/register`)}
              >
                {t("employee_register")}
              </Box>
              <Box
                className={`menu-item ${menu === SIDE_MENU.TAG_LIST ? "menu-item-selected" : ""}`}
                onClick={() => navigate(`/company/${user.companyID}/tag`)}
              >
                {t("tag_list")}
              </Box>
              <Box
                className={`menu-item ${menu === SIDE_MENU.COMPANY_INFO ? "menu-item-selected" : ""}`}
                onClick={() => navigate(`/company/${user.companyID}/info`)}
              >
                {t("company_setting")}
              </Box>
              <Box
                className={`menu-item ${menu === SIDE_MENU.MY_PAGE ? "menu-item-selected" : ""}`}
                onClick={() => navigate("/mypage")}
              >
                {t("my_page")}
              </Box>
              <Box
                className={`menu-item ${
                  menu === SIDE_MENU.PASSWORD_CHANGE ? "menu-item-selected" : ""
                }`}
                onClick={() => navigate("/password")}
              >
                {t("password_change")}
              </Box>
            </>
          ) : (
            <>
              <Box
                className={`menu-item ${menu === SIDE_MENU.MY_PAGE ? "menu-item-selected" : ""}`}
                onClick={() => navigate("/mypage")}
              >
                {t("my_page")}
              </Box>
              <Box
                className={`menu-item ${
                  menu === SIDE_MENU.PASSWORD_CHANGE ? "menu-item-selected" : ""
                }`}
                onClick={() => navigate("/password")}
              >
                {t("password_change")}
              </Box>
            </>
          )}
          <Typography className="menu-logout" onClick={() => handleLogout()}>
            {t("logout")}
          </Typography>
        </Box>
        <Box className="page-container">
          <Box className="content-container">{children}</Box>
        </Box>
      </Box>
    </>
  )
}
